import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

const ShopPublic = () => {
  const { handle } = useParams();

  const { data: shop } = useQuery({
    queryKey: ["shop-public", handle],
    queryFn: async () => {
      const { data } = await supabase
        .from("shops")
        .select("id, name, handle, bio, logo_url, location")
        .eq("handle", handle)
        .maybeSingle();
      return data;
    },
    enabled: !!handle,
  });

  const { data: products } = useQuery({
    queryKey: ["shop-products", shop?.id],
    queryFn: async () => {
      if (!shop?.id) return [] as any[];
      const { data } = await supabase
        .from("products")
        .select("id, title, price_cents, currency, status, product_images(url, sort)")
        .eq("shop_id", shop.id)
        .eq("status", "active")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!shop?.id,
  });

  if (!shop) {
    return (
      <main className="container mx-auto px-4 py-10">
        <h1 className="text-3xl font-heading font-bold mb-3">Shop</h1>
        <p className="text-muted-foreground">Loading shop…</p>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-10">
      <div className="flex items-start gap-4 mb-8">
        <div className="w-16 h-16 rounded-lg bg-muted border overflow-hidden">
          {shop.logo_url ? (
            <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No logo</div>
          )}
        </div>
        <div>
          <h1 className="text-3xl font-heading font-bold">{shop.name}</h1>
          <div className="text-muted-foreground">@{shop.handle}{shop.location ? ` · ${shop.location}` : ""}</div>
          {shop.bio && <p className="mt-2 max-w-2xl">{shop.bio}</p>}
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-4">Products</h2>
      {!products?.length ? (
        <div className="text-muted-foreground">No products yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {products.map((p: any) => (
            <Link key={p.id} to={`/products/${p.id}`} className="border rounded-xl p-3 hover:shadow-md transition">
              <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2">
                {p.product_images?.[0]?.url ? (
                  <img src={p.product_images[0].url} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                )}
              </div>
              <div className="text-sm font-medium line-clamp-1">{p.title}</div>
              <div className="text-xs text-muted-foreground">{p.currency} {(p.price_cents/100).toFixed(2)}</div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
};

export default ShopPublic;
